import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import GoogleSignIn from "../components/GoogleSignIn";
import { setSession } from "../utils/auth";

const getPasswordStrength = (password: string) => {
  if (password.length < 6) return "Weak";
  if (
    password.match(/[a-z]/) &&
    password.match(/[A-Z]/) &&
    password.match(/[0-9]/) &&
    password.length >= 8
  ) {
    return "Strong";
  }
  return "Moderate";
};

const getStrengthColor = (strength: string) => {
  switch (strength) {
    case "Weak":
      return "text-red-400";
    case "Moderate":
      return "text-amber-400";
    case "Strong":
      return "text-emerald-400";
    default:
      return "";
  }
};

const getStrengthBarWidth = (strength: string) => {
  switch (strength) {
    case "Weak":
      return "w-1/3";
    case "Moderate":
      return "w-2/3";
    case "Strong":
      return "w-full";
    default:
      return "w-0";
  }
};

const getStrengthBarColor = (strength: string) => {
  switch (strength) {
    case "Weak":
      return "bg-red-400";
    case "Moderate":
      return "bg-amber-400";
    case "Strong":
      return "bg-emerald-400";
    default:
      return "bg-zinc-700";
  }
};

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();

  const [firmName, setFirmName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  const [firmError, setFirmError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const passwordStrength = getPasswordStrength(password);

  const validateFirmName = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setFirmError("Firm or company name is required.");
      return false;
    }
    if (trimmed.length > 255) {
      setFirmError("Firm name must be 255 characters or fewer.");
      return false;
    }
    setFirmError("");
    return true;
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required.");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address.");
      return false;
    }
    setEmailError("");
    return true;
  };

  const validatePassword = (password: string): boolean => {
    if (!password) {
      setPasswordError("Password is required.");
      return false;
    } else if (password.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return false;
    }
    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (
    password: string,
    confirm: string,
  ): boolean => {
    if (password !== confirm) {
      setConfirmError("Passwords do not match.");
      return false;
    }
    setConfirmError("");
    return true;
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    const isFirmValid = validateFirmName(firmName);
    const isEmailValid = validateEmail(email);
    const isPasswordValid = validatePassword(password);
    const isConfirmValid = validateConfirmPassword(password, confirmPassword);

    if (!isFirmValid || !isEmailValid || !isPasswordValid || !isConfirmValid) return;

    setIsLoading(true);
    try {
      const res = await fetch(`https://api.adjusterphoto.com/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email,
          password,
          firm_name: firmName.trim(),
        }),
      });

      if (!res.ok) throw new Error("Registration failed");

      navigate("/login");
    } catch (err) {
      console.error(err);
      setError("Registration failed. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = (data: {
    status: string;
    token: string;
    refresh_token: string;
    [key: string]: unknown;
  }) => {
    if (data.status !== "ok") {
      setSuccess(
        "Account created! Your administrator will activate your account shortly.",
      );
      return;
    }

    setSession(data.token);
    navigate("/uploads");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0b0f] bg-grid-pattern relative overflow-hidden px-4">
      {/* Radial gradient overlay */}
      <div className="absolute inset-0 bg-radial-gradient pointer-events-none" />

      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-amber-400/5 rounded-full blur-3xl" />
      <div className="absolute bottom-32 left-16 w-80 h-80 bg-amber-400/3 rounded-full blur-3xl" />

      {/* Register card */}
      <div className="relative w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8 opacity-0 animate-slide-up stagger-1">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 mb-4 shadow-lg shadow-amber-500/20">
            <svg
              className="w-8 h-8 text-[#0a0b0f]"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
          </div>
          <h1 className="text-3xl font-semibold text-zinc-100 tracking-tight">
            Create Account
          </h1>
          <p className="text-zinc-500 mt-2 text-sm">
            Join Adjuster Photos today
          </p>
        </div>

        {/* Form card */}
        <div className="card-dark rounded-2xl p-8 shadow-2xl shadow-black/50 opacity-0 animate-slide-up stagger-2">
          {/* Firm name — required for both Google and email signup */}
          <div className="mb-5">
            <label htmlFor="firmName" className="block text-sm font-medium text-zinc-400 mb-2">
              Firm or company name
            </label>
            <input
              id="firmName"
              type="text"
              placeholder="e.g. Acme Adjusting LLC, or Independent Adjuster"
              value={firmName}
              onChange={(e) => setFirmName(e.target.value)}
              onBlur={() => validateFirmName(firmName)}
              className={`input-dark w-full px-4 py-3 rounded-xl text-zinc-100 ${firmError ? "border-red-500/50 focus:border-red-500" : ""}`}
              maxLength={255}
            />
            {firmError ? (
              <p className="text-red-400 text-sm mt-2">{firmError}</p>
            ) : (
              <p className="text-zinc-500 text-xs mt-2">
                Solo? Enter your own business name or "Independent Adjuster". You can change this later.
              </p>
            )}
          </div>

          {/* Google Sign-In — Primary */}
          <div className="mb-4 opacity-0 animate-slide-up stagger-3">
            <GoogleSignIn
              onSuccess={handleGoogleSuccess}
              onError={(msg) => setError(msg)}
              disabled={isLoading || !firmName.trim()}
              firmName={firmName}
            />
          </div>

          <p className="text-center text-zinc-500 text-xs mb-4">
            {firmName.trim()
              ? "Create your account instantly with Google"
              : "Enter your firm name above to enable Google sign-in"}
          </p>

          {/* Success message */}
          {success && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl px-4 py-3 text-emerald-400 text-sm text-center mb-4">
              {success}
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3 text-red-400 text-sm text-center mb-4">
              {error}
            </div>
          )}

          {/* Email registration — collapsed by default */}
          {!showEmailForm ? (
            <div className="text-center pt-2 border-t border-zinc-700/30">
              <button
                onClick={() => setShowEmailForm(true)}
                className="text-zinc-500 hover:text-zinc-300 text-sm transition-colors mt-3"
              >
                Register with email and password instead
              </button>
            </div>
          ) : (
            <>
              <div className="relative mb-5 mt-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-zinc-700/50" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 text-zinc-500 bg-[var(--color-bg-card)]">
                    or register with email
                  </span>
                </div>
              </div>

              <form onSubmit={handleRegister} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-zinc-400 mb-2">
                    Email address
                  </label>
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => validateEmail(email)}
                    className={`input-dark w-full px-4 py-3 rounded-xl text-zinc-100 ${emailError ? "border-red-500/50 focus:border-red-500" : ""}`}
                  />
                  {emailError && <p className="text-red-400 text-sm mt-2">{emailError}</p>}
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-zinc-400 mb-2">
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    placeholder="Create a strong password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => validatePassword(password)}
                    className={`input-dark w-full px-4 py-3 rounded-xl text-zinc-100 ${passwordError ? "border-red-500/50 focus:border-red-500" : ""}`}
                  />
                  {password && (
                    <div className="mt-3">
                      <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                        <div className={`h-full ${getStrengthBarColor(passwordStrength)} ${getStrengthBarWidth(passwordStrength)} transition-all duration-300`} />
                      </div>
                      <p className={`text-sm mt-2 ${getStrengthColor(passwordStrength)}`}>{passwordStrength} password</p>
                    </div>
                  )}
                  {passwordError && <p className="text-red-400 text-sm mt-2">{passwordError}</p>}
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-zinc-400 mb-2">
                    Confirm password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    placeholder="Re-enter your password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onBlur={() => validateConfirmPassword(password, confirmPassword)}
                    className={`input-dark w-full px-4 py-3 rounded-xl text-zinc-100 ${confirmError ? "border-red-500/50 focus:border-red-500" : ""}`}
                  />
                  {confirmError && <p className="text-red-400 text-sm mt-2">{confirmError}</p>}
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="btn-primary w-full py-3.5 rounded-xl text-base disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {isLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Creating account...
                      </span>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </div>
              </form>
            </>
          )}

          {/* Login link */}
          <p className="text-center text-zinc-500 text-sm pt-4">
            Already have an account?{" "}
            <a href="/#login" className="text-amber-400 hover:text-amber-300 hover:underline">
              Sign in
            </a>
          </p>
        </div>

        {/* Footer */}
        <p className="text-center text-zinc-600 text-sm mt-6 opacity-0 animate-fade-in stagger-5">
          Secure photo management for insurance professionals
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
